// Device / display-mode detection shared by the chrome runtime (navbar
// fullscreen toggle, PWA fullscreen coercion) and the fullscreen hint.

type FsElement = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
};
type FsDocument = Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
};

const mediaMatches = (query: string): boolean => {
    try {
        return window.matchMedia(query).matches;
    } catch {
        return false;
    }
};

/**
 * Running as an installed PWA (standalone / fullscreen / minimal-ui window, or
 * an iOS home-screen app) rather than in a browser tab.
 */
export function isInstalledPwa(): boolean {
    if (
        mediaMatches('(display-mode: standalone)') ||
        mediaMatches('(display-mode: minimal-ui)') ||
        mediaMatches('(display-mode: fullscreen)')
    ) {
        return true;
    }
    return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * Small phone, as opposed to a tablet or desktop: shortest screen edge under
 * 600 CSS px (Android's tablet threshold). Fullscreen affordances are skipped
 * there — more nuisance than help.
 */
export function isSmallPhone(): boolean {
    try {
        const w = window.screen?.width ?? window.innerWidth;
        const h = window.screen?.height ?? window.innerHeight;
        return Math.min(w, h) < 600;
    } catch {
        return false;
    }
}

/** Whole-document fullscreen is available (excludes iOS Safari). */
export function fullscreenSupported(): boolean {
    const el = document.documentElement as FsElement;
    return !!(el.requestFullscreen || el.webkitRequestFullscreen);
}

/** Some element is fullscreen through the Fullscreen API. */
export function isApiFullscreen(): boolean {
    const d = document as FsDocument;
    return !!(d.fullscreenElement || d.webkitFullscreenElement);
}

/**
 * The viewport is fullscreen for *any* reason, including the browser's own
 * F11 / OS fullscreen. F11 never uses the Fullscreen API, so it leaves
 * `document.fullscreenElement` null and fires no `fullscreenchange` — but it
 * does flip the `display-mode: fullscreen` media query, which the API path
 * also matches.
 */
export function isAnyFullscreen(): boolean {
    return isApiFullscreen() || mediaMatches('(display-mode: fullscreen)');
}

/**
 * Request fullscreen on `documentElement`. Resolves when it engaged, rejects
 * when the browser refused (e.g. no user gesture). Never throws.
 */
export function requestDocumentFullscreen(): Promise<void> {
    const el = document.documentElement as FsElement;
    try {
        const req = el.requestFullscreen ? el.requestFullscreen() : el.webkitRequestFullscreen?.();
        return Promise.resolve(req);
    } catch (err) {
        return Promise.reject(err);
    }
}

export function exitDocumentFullscreen(): void {
    const d = document as FsDocument;
    try {
        if (document.exitFullscreen) void document.exitFullscreen().catch(() => {});
        else void d.webkitExitFullscreen?.();
    } catch {
        /* fullscreen is a nice-to-have */
    }
}
